/** Friendly tool-call renderer for ChatBox `dynamic-tool` parts: plain-language line + status icon, replacing the library's raw JSON default. */

import { CheckCircle, ErrorOutlined, ExpandMore, Insights } from "@mui/icons-material"
import { Box, Collapse, IconButton, Typography, styled } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { useState } from "react"
import type { ChatDynamicToolMessagePart } from "@mui/x-chat-headless"
import type { ChatPartRendererProps } from "@mui/x-chat-headless"
import { toolDisplayLabel } from "./tool-labels"

const ToolRoot = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.75, 1),
  margin: theme.spacing(0.5, 0),
  backgroundColor: theme.palette.action.hover,
}))

const ToolSummary = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}))

const ToolDetails = styled("pre")(({ theme }) => ({
  margin: theme.spacing(1, 0, 0),
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.secondary,
  fontSize: theme.typography.caption.fontSize,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  overflowX: "auto",
}))

const TOOL_LABEL_SX = { flex: 1, minWidth: 0 } satisfies SxProps<Theme>
const CHEVRON_SX = { transition: "transform 120ms" } satisfies SxProps<Theme>
const CHEVRON_OPEN_SX = { transform: "rotate(180deg)" } satisfies SxProps<Theme>

function pretty(value: unknown): string {
  if (value == null) return ""
  if (typeof value === "string") return value
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

export function ToolPart({ part, message }: ChatPartRendererProps<ChatDynamicToolMessagePart>) {
  const [open, setOpen] = useState(false)
  const invocation = part.toolInvocation
  if (!invocation) return null

  const { toolName, state, input, output, errorText } = invocation
  const label = toolDisplayLabel(invocation.title, toolName)
  const isDone = state === "output-available"
  const isSettled = message.status !== "streaming"
  const isError = state === "output-error" || state === "output-denied" || (!isDone && isSettled)
  const running = !isDone && !isError
  const details = errorText || pretty(output) || pretty(input)

  return (
    <ToolRoot data-testid="chatbox-tool-part">
      <ToolSummary>
        {isError && <ErrorOutlined fontSize="small" color="error" />}
        {isDone && <CheckCircle fontSize="small" color="success" />}
        {running && <Insights fontSize="small" color="primary" />}
        <Typography variant="body2" sx={TOOL_LABEL_SX}>
          {running ? `${label}…` : label}
          {isDone && (
            <Typography component="span" variant="caption" color="text.secondary">
              {" · Omega Point"}
            </Typography>
          )}
        </Typography>
        {details && (
          <IconButton
            size="small"
            onClick={() => setOpen((previous) => !previous)}
            aria-label={open ? "Hide details" : "Show details"}
          >
            <ExpandMore fontSize="small" sx={[CHEVRON_SX, open && CHEVRON_OPEN_SX]} />
          </IconButton>
        )}
      </ToolSummary>
      {details && (
        <Collapse in={open} unmountOnExit>
          <ToolDetails>{details}</ToolDetails>
        </Collapse>
      )}
    </ToolRoot>
  )
}
